import React, { useState, useMemo, useEffect } from "react";
import { Tree, Button } from "antd";
import { useDispatch, useSelector } from "react-redux";

import {
  setOpportunityTypeFilters,
  setOpportunityTypeFilterCount,
} from "../../../../redux/slices/leadSlice";
import CustomSkeleton from "../../../commonComponents/CustomSkeleton";
import useOpportunityTypeTree from "../../../../hooks/useOpportunityTypeTree";

// Count checked keys whose parent is not also checked (i.e. top-level logical selections)
const computeParentCount = (checkedKeys, treeData) => {
  const parentMap = {};
  const buildMap = (nodes, parentKey = null) => {
    nodes.forEach(node => {
      parentMap[String(node.key)] = parentKey;
      if (node.children?.length > 0) buildMap(node.children, String(node.key));
    });
  };
  buildMap(treeData);
  const checkedSet = new Set(checkedKeys.map(String));
  return checkedKeys.filter(key => {
    const parent = parentMap[String(key)];
    return !parent || !checkedSet.has(parent);
  }).length;
};

const toAntTreeData = list =>
  list.map(item => ({
    title: item.name,
    key: String(item.key),
    children: item.children?.length > 0 ? toAntTreeData(item.children) : undefined,
  }));

const OpportunityTypeContent = ({ search = "", scrollListToTop, onClear }) => {
  const dispatch = useDispatch();
  const opportunityTypeFilters =
    useSelector(state => state.lead.filters.opportunityTypeFilters) || [];
  const [expandedNodes, setExpandedNodes] = useState([]);

  const { loading, treeData } = useOpportunityTypeTree();

  const antTreeData = useMemo(() => toAntTreeData(treeData), [treeData]);

  const filteredTreeData = useMemo(() => {
    if (!search) return antTreeData;
    const lowerSearch = search.toLowerCase();
    const filterNodes = nodes =>
      nodes
        .map(node => {
          const filteredChildren = node.children ? filterNodes(node.children) : [];
          const selfMatch = node.title?.toLowerCase().includes(lowerSearch);
          if (selfMatch || filteredChildren.length > 0) {
            return {
              ...node,
              children: filteredChildren.length > 0 ? filteredChildren : node.children,
            };
          }
          return null;
        })
        .filter(Boolean);
    return filterNodes(antTreeData);
  }, [antTreeData, search]);

  useEffect(() => {
    if (search) {
      const keys = [];
      const collect = nodes =>
        (nodes || []).forEach(n => {
          if (n.children?.length > 0) {
            keys.push(n.key);
            collect(n.children);
          }
        });
      collect(filteredTreeData);
      setExpandedNodes(keys);
    }
  }, [filteredTreeData, search]);

  const allParentKeys = useMemo(() => {
    const keys = [];
    const collect = nodes =>
      (nodes || []).forEach(n => {
        if (n.children?.length > 0) {
          keys.push(n.key);
          collect(n.children);
        }
      });
    collect(filteredTreeData);
    return keys;
  }, [filteredTreeData]);

  const isAllExpanded = expandedNodes.length >= allParentKeys.length;

  if (loading) {
    return (
      <div className="px-3 py-2">
        <CustomSkeleton
          skeletonCount={5}
          skeletonMarginBottom="7px"
          skeletonWidth="28rem"
          skeletonHeight="2rem"
        />
      </div>
    );
  }

  return (
    <div className="px-3 py-2">
      <div className="flex items-center justify-between mb-2">
        {allParentKeys.length > 0 ? (
          <Button
            type="link"
            size="small"
            className="!p-0 !h-auto"
            onClick={() => setExpandedNodes(isAllExpanded ? [] : allParentKeys)}
          >
            {isAllExpanded ? "Collapse all" : "Expand all"}
          </Button>
        ) : (
          <span />
        )}
        {onClear && opportunityTypeFilters.length > 0 && (
          <Button type="link" size="small" className="!p-0 !h-auto" onClick={onClear}>
            Clear
          </Button>
        )}
      </div>
      <Tree
        checkable
        checkedKeys={opportunityTypeFilters.map(String)}
        expandedKeys={expandedNodes}
        onExpand={keys => setExpandedNodes([...keys])}
        onCheck={checkedKeys => {
          const keys = (Array.isArray(checkedKeys) ? checkedKeys : []).map(Number);
          dispatch(setOpportunityTypeFilters(keys));
          dispatch(setOpportunityTypeFilterCount(computeParentCount(keys, antTreeData)));
          scrollListToTop?.();
        }}
        treeData={filteredTreeData}
      />
    </div>
  );
};

export default OpportunityTypeContent;
